import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: "home", key: "home" },
  { id: "chi-siamo", key: "about" },
  { id: "attivita", key: "activities" },
  { id: "cosa-puoi-fare", key: "help" },
  { id: "volontariato", key: "volunteering", route: "/volontariato" },
  { id: "news", key: "news" },
  { id: "contatti", key: "contact" },
];

export default function Footer({ scrollTo }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, language: lang, origin_url: window.location.origin });
      toast.success(t.footer.subscribed);
      setEmail("");
    } catch {
      toast.error(t.footer.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#020b1c]">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 md:px-8">
        <div className="flex flex-col justify-between gap-12 border-b border-white/10 pb-16 lg:flex-row lg:items-end">
          <div className="max-w-md">
            <h3 className="font-serif text-3xl tracking-tight text-white md:text-4xl">
              {t.footer.newsletterTitle}
            </h3>
            <p className="mt-3 text-sm text-white/55 md:text-base">{t.footer.newsletterSub}</p>
            <form onSubmit={subscribe} className="mt-6 flex gap-3">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.footer.placeholder}
                className="w-full rounded-full border border-white/15 bg-white/[0.05] px-6 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
              <button
                data-testid="newsletter-submit-button"
                type="submit"
                disabled={loading}
                className="shrink-0 rounded-full bg-primary px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#e0562a] disabled:opacity-60"
              >
                {t.footer.subscribe}
              </button>
            </form>
          </div>
          <nav className="flex flex-wrap gap-x-8 gap-y-3" aria-label="Footer">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`footer-nav-${item.id}`}
                onClick={() => (item.route ? navigate(item.route) : scrollTo(item.id))}
                className="text-[12px] font-medium uppercase tracking-[0.16em] text-white/55 transition-colors hover:text-white"
              >
                {t.nav[item.key]}
              </button>
            ))}
          </nav>
        </div>

        <div className="select-none overflow-hidden py-10 text-center" aria-hidden="true">
          <span className="font-serif text-[16vw] leading-[0.85] tracking-tight text-white/[0.07] lg:text-[11rem]">
            You4Mada
          </span>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-xs text-white/40" data-testid="footer-rights">
            {t.footer.rights}
          </p>
          <p className="font-serif text-sm italic text-primary/80" data-testid="footer-tagline">
            {t.footer.tagline}
          </p>
        </div>
      </div>
    </footer>
  );
}
